<template>
  <div
    class="w-full mt-6 bg-gray-50 dark:bg-background rounded-t-[20px] flex flex-col px-4 md:px-8 gap-6 lg:gap-8 min-h-screen"
  >
    <div class="flex flex-col lg:flex-row lg:items-start w-full gap-8">
      <div class="w-full lg:w-32 flex-shrink-0 lg:pt-2">
        <BackButton />
      </div>

      <!-- Loading State -->
      <div
        v-if="loading"
        class="flex-1 w-full flex items-center justify-center py-40"
      >
        <Loader />
      </div>

      <!-- Main Content Area -->
      <div v-else-if="selectedHub" class="flex-1 w-full min-w-0">
        <div class="space-y-4 pb-20 py-4">
          <div
            class="hr-container sticky top-0 z-30 bg-gray-50 dark:bg-background space-y-4"
          >
            <div class="flex items-start justify-between">
              <div class="space-y-3 flex-1">
                <p
                  class="font-semibold text-foreground text-2xl md:text-[36px] leading-tight flex-1"
                >
                  {{ selectedHub.name }}
                </p>
              </div>
            </div>

            <div
              class="flex flex-col lg:flex-row items-center gap-4 flex-wrap w-full"
            >
              <div class="flex flex-wrap items-center gap-4">
                <!-- Pill Tabs -->
                <div
                  class="flex p-1 bg-muted/30 rounded-full border border-border"
                >
                  <button
                    v-for="tab in tabs"
                    :key="tab"
                    @click="activeTab = tab"
                    class="px-5 py-2 rounded-full text-sm font-medium transition-all"
                    :class="
                      activeTab === tab
                        ? 'bg-card text-foreground shadow-sm dark:bg-muted dark:text-white'
                        : 'text-muted-foreground hover:text-foreground'
                    "
                  >
                    {{ tab }}
                  </button>
                </div>

                <!-- Switch Hub Dropdown -->
                <div class="relative" v-click-outside="closeHubDropdown">
                  <button
                    @click="toggleHubDropdown"
                    class="h-10 px-4 rounded-full bg-card border border-border text-sm font-medium flex items-center gap-2 hover:bg-muted transition-colors"
                  >
                    <LayoutDashboard class="w-4 h-4 text-primary" />
                    Switch Hub
                    <ChevronDown
                      class="w-4 h-4 text-muted-foreground"
                      :class="{ 'rotate-180': showHubDropdown }"
                    />
                  </button>

                  <transition name="fade">
                    <div
                      v-if="showHubDropdown"
                      class="absolute left-0 mt-2 w-64 bg-card border border-border rounded-[16px] shadow-lg py-2 z-50 overflow-hidden"
                    >
                      <div
                        v-for="hub in hubs"
                        :key="hub.id"
                        @click="selectHub(hub)"
                        class="px-4 py-3 flex items-center gap-3 hover:bg-muted cursor-pointer transition-colors"
                        :class="[{ 'bg-muted/50': selectedHub.id === hub.id }]"
                      >
                        <div
                          class="w-8 h-8 rounded-lg flex items-center justify-center bg-muted text-foreground font-bold text-sm border border-border/50"
                        >
                          {{ hub.name.charAt(0) }}
                        </div>
                        <div class="flex-1 min-w-0">
                          <p class="text-sm font-semibold truncate">
                            {{ hub.name }}
                          </p>
                          <p class="text-xs text-muted-foreground">
                            {{ hub.members }} members
                          </p>
                        </div>
                        <Check
                          v-if="selectedHub.id === hub.id"
                          class="w-4 h-4 text-primary"
                        />
                      </div>
                      <div class="border-t border-border mt-2 pt-2">
                        <button
                          @click="
                            isEditingHub = false;
                            showCreateHubModal = true;
                          "
                          class="w-full px-4 py-3 text-sm font-medium text-primary flex items-center gap-2 hover:bg-muted transition-colors"
                        >
                          <Plus class="w-4 h-4" />
                          Create New Hub
                        </button>
                      </div>
                    </div>
                  </transition>
                </div>

                <LearningGoalActionsBar
                  :goal="null"
                  :projectId="selectedHub.id"
                  @book-mentor="onBookMentor"
                  @edit-goal="onEditGoal"
                  @delete="showDeleteHubModal = true"
                />
              </div>

              <!-- Page Actions -->
              <div
                class="flex items-center gap-2 flex-wrap lg:ml-auto"
                v-if="isOwner"
              >
                <div class="relative" v-click-outside="closeActionsDropdown">
                  <button
                    type="button"
                    @click="toggleActionsDropdown"
                    class="w-10 h-10 rounded-full bg-transparent border border-border flex items-center justify-center hover:bg-muted transition-colors"
                  >
                    <EllipsisVertical class="w-5 h-5" />
                  </button>

                  <transition name="fade">
                    <div
                      v-if="showActionsDropdown"
                      class="absolute right-0 mt-2 w-48 bg-card border border-border rounded-[12px] shadow-lg py-1 z-50 overflow-hidden"
                    >
                      <button
                        v-if="isOwner"
                        @click="onEditGoal"
                        class="w-full flex items-center gap-2 px-4 py-3 text-[14px] hover:bg-muted transition-colors text-left"
                      >
                        <Settings class="w-4 h-4" />
                        Hub Settings
                      </button>
                      <button
                        v-if="isOwner"
                        @click="onDeleteHub"
                        class="w-full flex items-center gap-2 px-4 py-3 text-[14px] text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors text-left"
                      >
                        <Trash2 class="w-4 h-4" />
                        Delete Hub
                      </button>
                    </div>
                  </transition>
                </div>
              </div>
            </div>
          </div>

          <!-- Tab Content Sections -->
          <div
            class="animate-in fade-in slide-in-from-bottom-4 duration-500 border p-4 rounded-[12px] dark:bg-background"
          >
            <!-- Overview Tab -->
            <div v-if="activeTab === 'Overview'" class="space-y-12">
              <div class="space-y-4">
                <h2 class="text-[15px] font-semibold text-foreground">
                  About Hub
                </h2>
                <div class="space-y-4 pb-4 border-b border-border/30">
                  <p
                    class="text-[14px] text-foreground/80 leading-relaxed max-w-4xl"
                  >
                    {{ selectedHub.description }}
                  </p>
                  <div
                    class="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 text-sm text-muted-foreground pt-2"
                  >
                    <span
                      class="flex items-center gap-1.5"
                      v-if="selectedHub.address"
                    >
                      <MapPin class="w-4 h-4 text-primary" />
                      {{ selectedHub.address }}
                    </span>
                    <a
                      :href="selectedHub.website"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="flex items-center gap-1.5 hover:text-primary transition-colors"
                      v-if="selectedHub.website"
                    >
                      <Globe class="w-4 h-4 text-primary" />
                      {{ selectedHub.website.replace(/^https?:\/\//, "") }}
                    </a>
                  </div>
                </div>

                <!-- Amenities Tags -->
                <div
                  v-if="selectedHub.amenities && selectedHub.amenities.length"
                  class="space-y-2"
                >
                  <h2 class="text-[15px] font-semibold text-foreground">
                    Amenities
                  </h2>
                  <div class="flex flex-wrap gap-2">
                    <span
                      v-for="amenity in selectedHub.amenities"
                      :key="amenity"
                      class="px-4 py-2 bg-muted/20 border border-border rounded-full text-[13px] font-medium"
                    >
                      {{ amenity }}
                    </span>
                  </div>
                </div>

                <!-- Metrics Row -->
                <div
                  class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 mt-4"
                >
                  <div class="space-y-2">
                    <p
                      class="text-[11px] font-bold text-muted-foreground/60 uppercase tracking-widest"
                    >
                      Opening Hours
                    </p>
                    <p class="text-[16px] font-bold text-foreground">
                      {{ formatOpeningHours(selectedHub.operating_hours) }}
                    </p>
                  </div>
                  <div class="space-y-2">
                    <p
                      class="text-[11px] font-bold text-muted-foreground/60 uppercase tracking-widest"
                    >
                      Pricing
                    </p>
                    <p class="text-[16px] font-bold text-foreground">
                      {{ formatPricing(selectedHub.pricing) }}
                    </p>
                  </div>
                  <div class="space-y-2">
                    <p
                      class="text-[11px] font-bold text-muted-foreground/60 uppercase tracking-widest"
                    >
                      Rating
                    </p>
                    <div class="flex items-center gap-2">
                      <p class="text-[16px] font-bold text-foreground">4.9</p>
                    </div>
                  </div>
                  <div class="space-y-2">
                    <p
                      class="text-[11px] font-bold text-muted-foreground/60 uppercase tracking-widest"
                    >
                      Capacity
                    </p>
                    <div class="flex items-center gap-2">
                      <p class="text-[16px] font-bold text-foreground">
                        {{ selectedHub.capacity || "N/A" }} People
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Contact & Support (Important UX Area) -->
                <div
                  v-if="selectedHub.contact_info"
                  class="mt-8 pt-6 border-t border-border/30"
                >
                  <h2 class="text-[15px] font-semibold text-foreground mb-4">
                    Contact & Support
                  </h2>
                  <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div
                      class="bg-muted/10 border border-border rounded-xl p-4 flex flex-col justify-center"
                    >
                      <div class="flex items-center gap-3 mb-1">
                        <div
                          class="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary"
                        >
                          <Mail class="w-4 h-4" />
                        </div>
                        <p class="text-sm font-semibold">Email</p>
                      </div>
                      <a
                        :href="`mailto:${selectedHub.contact_info.email}`"
                        class="text-sm text-muted-foreground hover:text-primary pl-11"
                        >{{ selectedHub.contact_info.email }}</a
                      >
                    </div>
                    <div
                      class="bg-muted/10 border border-border rounded-xl p-4 flex flex-col justify-center"
                    >
                      <div class="flex items-center gap-3 mb-1">
                        <div
                          class="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary"
                        >
                          <Phone class="w-4 h-4" />
                        </div>
                        <p class="text-sm font-semibold">Phone</p>
                      </div>
                      <a
                        :href="`tel:${selectedHub.contact_info.phone}`"
                        class="text-sm text-muted-foreground hover:text-primary pl-11"
                        >{{ selectedHub.contact_info.phone }}</a
                      >
                    </div>
                    <div
                      class="bg-muted/10 border border-border rounded-xl p-4 flex flex-col justify-center"
                    >
                      <div class="flex items-center gap-3 mb-1">
                        <div
                          class="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary"
                        >
                          <User class="w-4 h-4" />
                        </div>
                        <p class="text-sm font-semibold">Manager</p>
                      </div>
                      <p class="text-sm text-muted-foreground pl-11">
                        {{ selectedHub.contact_info.manager }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Availability Tab -->
            <div v-if="activeTab === 'Availability'">
              <AvailabilitySettings
                v-model="selectedHub.operating_hours"
                :isSaving="isSavingAvailability"
                :editable="isOwner"
                @save="saveAvailability"
                description="Configure when this hub is open for bookings."
              />
            </div>

            <!-- Schedule Tab -->
            <div v-if="activeTab === 'Schedule'" class="space-y-6">
              <h3 class="text-[14px] font-semibold mb-2">Accepted Bookings</h3>
              <div v-if="acceptedBookings.length > 0" class="space-y-4">
                <div
                  v-for="booking in acceptedBookings"
                  :key="booking.id"
                  class="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-card border border-border rounded-xl hover:shadow-sm transition-all gap-4"
                >
                  <div class="flex items-center gap-4">
                    <div
                      class="w-10 h-10 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center shrink-0"
                    >
                      {{ (booking.user_name || "U").charAt(0) }}
                    </div>
                    <div>
                      <p class="text-[15px] font-semibold text-foreground">
                        {{ booking.user_name }}
                      </p>
                      <p
                        class="text-[13px] font-medium text-foreground/80 mt-0.5"
                      >
                        {{ booking.topic || "Workspace Booking" }}
                      </p>
                      <p class="text-[12px] text-muted-foreground">
                        {{ formatDateTime(booking.booking_date) }}
                      </p>
                    </div>
                  </div>
                  <div
                    class="px-3 py-1 bg-green-500/10 text-green-600 rounded-full text-[11px] font-bold uppercase tracking-wider border border-green-500/20"
                  >
                    Accepted
                  </div>
                </div>
              </div>
              <div
                v-else
                class="border border-dashed border-border rounded-[20px] p-12 text-center space-y-4"
              >
                <Clock class="w-12 h-12 mx-auto text-muted-foreground/30" />
                <p class="text-muted-foreground">
                  Your schedule is currently clear. <br />Bookings from talents
                  will appear here.
                </p>
              </div>
            </div>

            <!-- Requests Tab -->
            <div v-if="activeTab === 'Requests'" class="space-y-6">
              <h3 class="text-[14px] font-semibold mb-2">Pending Requests</h3>

              <div
                v-if="isFetchingRequests"
                class="flex items-center justify-center py-20"
              >
                <Loader />
              </div>

              <div
                v-else-if="workspaceRequests && workspaceRequests.length > 0"
                class="space-y-4"
              >
                <div
                  v-for="request in workspaceRequests"
                  :key="request.id"
                  class="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-card border border-border rounded-xl hover:shadow-sm transition-all gap-4"
                >
                  <div class="flex items-center gap-4">
                    <div
                      class="w-10 h-10 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center shrink-0"
                    >
                      {{ (request.user_name || "U").charAt(0) }}
                    </div>
                    <div>
                      <p class="text-[15px] font-semibold text-foreground">
                        {{ request.user_name }}
                      </p>
                      <p
                        class="text-[13px] font-medium text-foreground/80 mt-0.5"
                      >
                        {{ request.topic || "Hub Request" }}
                      </p>
                      <p class="text-[12px] text-muted-foreground">
                        {{ formatDateTime(request.booking_date) }}
                      </p>
                    </div>
                  </div>
                  <div class="flex items-center gap-2 w-full sm:w-auto">
                    <button
                      @click="viewRequestDetails(request)"
                      class="flex-1 sm:flex-none px-4 py-2 text-[13px] font-semibold text-foreground hover:bg-muted transition-colors rounded-full border border-border"
                    >
                      View Details
                    </button>
                    <button
                      @click="onDeclineRequest(request)"
                      class="flex-1 sm:flex-none px-5 py-2 border border-border rounded-full text-[13px] font-medium text-foreground hover:bg-muted transition-colors"
                    >
                      Decline
                    </button>
                    <button
                      @click="onApproveRequest(request)"
                      class="flex-1 sm:flex-none px-5 py-2 bg-primary text-primary-foreground rounded-full text-[13px] font-bold hover:opacity-90 transition-opacity whitespace-nowrap"
                    >
                      Approve
                    </button>
                  </div>
                </div>
              </div>

              <div
                v-else
                class="flex flex-col items-center justify-center py-20 text-center space-y-6"
              >
                <div
                  class="w-20 h-20 bg-muted/30 rounded-full flex items-center justify-center"
                >
                  <Inbox class="w-10 h-10 text-muted-foreground/50" />
                </div>
                <div class="space-y-1">
                  <h4 class="text-xl font-bold">All caught up!</h4>
                  <p class="text-muted-foreground">
                    You have no pending requests to review right now.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Hub Modal -->
    <WorkspaceCreateModal
      :isOpen="showCreateHubModal"
      :hub="isEditingHub ? selectedHub : null"
      @close="showCreateHubModal = false"
      @created="onHubCreated"
      @updated="onHubUpdated"
    />

    <!-- Workspace Detail Modal -->
    <HubRequestDetailsModal
      v-if="selectedRequest"
      :isOpen="!!selectedRequest"
      :request="selectedRequest"
      @close="selectedRequest = null"
      @approve="onApproveRequest"
      @decline="onDeclineRequest"
    />

    <!-- Delete Hub Modal -->
    <DeleteWorkspaceModal
      :isOpen="showDeleteHubModal"
      :workspaceId="selectedHub?.id"
      :workspaceName="selectedHub?.name"
      @close="showDeleteHubModal = false"
      @confirm-delete="onConfirmDeleteHub"
    />
  </div>
</template>

<script>
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import apiClient from "@/axios";
import BackButton from "@/components/BackButton.vue";
import Loader from "@/components/ui/Loader.vue";
import AvailabilitySettings from "@/components/dashboard/AvailabilitySettings.vue";
import WorkspaceCreateModal from "@/components/modals/WorkspaceCreateModal.vue";
import HubRequestDetailsModal from "@/components/modals/HubRequestDetailsModal.vue";
import DeleteWorkspaceModal from "@/components/modals/DeleteWorkspaceModal.vue";
import {
  Check,
  Settings,
  Plus,
  EllipsisVertical,
  Trash2,
  ChevronDown,
  LayoutDashboard,
  Inbox,
  Clock,
  // CalendarCheck,
  Globe,
  MapPin,
  Mail,
  Phone,
  User,
} from "lucide-vue-next";
import LearningGoalActionsBar from "@/components/learning-goals/LearningGoalActionsBar.vue";

dayjs.extend(relativeTime);

export default {
  name: "HubsView",
  components: {
    WorkspaceCreateModal,
    BackButton,
    Check,
    Settings,
    LearningGoalActionsBar,
    Loader,
    Plus,
    Trash2,
    EllipsisVertical,
    ChevronDown,
    LayoutDashboard,
    Inbox,
    Clock,
    // CalendarCheck,
    Globe,
    MapPin,
    Mail,
    Phone,
    User,
    AvailabilitySettings,
    HubRequestDetailsModal,
    DeleteWorkspaceModal,
  },
  directives: {
    "click-outside": {
      beforeMount(el, binding) {
        el.clickOutsideEvent = function (event) {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value();
          }
        };
        document.addEventListener("click", el.clickOutsideEvent);
      },
      unmounted(el) {
        document.removeEventListener("click", el.clickOutsideEvent);
      },
    },
  },
  data() {
    return {
      showCreateHubModal: false,
      showHubDropdown: false,
      showActionsDropdown: false,
      selectedHub: null,
      hubs: [], // Used for Switch Hub dropdown
      loading: true,
      isEditingHub: false,
      activeTab: "Overview",
      tabs: ["Overview", "Availability", "Schedule", "Requests"],
      hubMembers: [],
      recentActivity: [],
      isSavingAvailability: false,
      selectedRequest: null,
      workspaceRequests: [],
      isFetchingRequests: false,
      acceptedBookings: [],
      showDeleteHubModal: false,
    };
  },
  computed: {
    isOwner() {
      if (!this.selectedHub) return false;
      const currentUser = this.$store.getters.currentUser;
      return (
        this.selectedHub.is_owner === true ||
        (currentUser && this.selectedHub.owner_id === currentUser.user_id) ||
        (currentUser && this.selectedHub.talent_id === currentUser.user_id)
      );
    },
  },
  methods: {
    toggleHubDropdown() {
      this.showHubDropdown = !this.showHubDropdown;
      if (this.showHubDropdown) this.showActionsDropdown = false;
    },
    closeHubDropdown() {
      this.showHubDropdown = false;
    },
    toggleActionsDropdown() {
      this.showActionsDropdown = !this.showActionsDropdown;
      if (this.showActionsDropdown) this.showHubDropdown = false;
    },
    closeActionsDropdown() {
      this.showActionsDropdown = false;
    },
    async fetchHubDetails() {
      const id = this.$route.params.id;
      if (!id) return;
      try {
        this.loading = true;
        const response = await apiClient.get(`/web/workspace/${id}`);
        this.selectedHub = response.data?.data || response.data;
      } catch (error) {
        console.error("Error fetching hub details:", error);
        this.$toast?.error("Error", "Failed to load hub details.");
        this.$router.push("/my-hubs");
      } finally {
        this.loading = false;
        if (this.selectedHub) {
          this.fetchWorkspaceRequests();
          this.fetchAcceptedBookings();
        }
      }
    },
    async fetchAcceptedBookings() {
      if (!this.selectedHub) return;
      try {
        const response = await apiClient.get(
          `/web/workspace/${this.selectedHub.id}/requests`,
          {
            params: { request_status: "approved" },
          },
        );
        this.acceptedBookings = response.data?.data || response.data || [];
      } catch (error) {
        console.error("Error fetching accepted bookings:", error);
      }
    },
    async fetchWorkspaceRequests() {
      if (!this.selectedHub) return;
      try {
        this.isFetchingRequests = true;
        const response = await apiClient.get(
          `/web/workspace/${this.selectedHub.id}/requests`,
          {
            params: { request_status: "pending" },
          },
        );
        this.workspaceRequests = response.data?.data || response.data || [];
      } catch (error) {
        console.error("Error fetching workspace requests:", error);
      } finally {
        this.isFetchingRequests = false;
      }
    },
    async fetchAllHubs() {
      // For the Switch Hub dropdown
      try {
        const response = await apiClient.get("/web/workspace");
        this.hubs = response.data?.data || response.data || [];
      } catch (error) {
        console.error("Error fetching hubs list:", error);
      }
    },
    selectHub(hub) {
      this.$router.push(`/my-hubs/${hub.id}`);
      this.showHubDropdown = false;
    },
    onHubCreated(newHub) {
      this.showCreateHubModal = false;
      this.$toast?.success("Success", "New hub created successfully!");
      this.fetchAllHubs();
      if (newHub && newHub.id) {
        this.$router.push(`/my-hubs/${newHub.id}`);
      }
    },
    onHubUpdated() {
      this.showCreateHubModal = false;
      this.$toast?.success("Success", "Hub updated successfully!");
      this.fetchHubDetails();
      this.fetchAllHubs();
    },
    onInvite() {
      console.log("Inviting to hub...");
    },
    onDeleteHub() {
      this.showDeleteHubModal = true;
    },
    async onConfirmDeleteHub(hubId) {
      try {
        await apiClient.delete(`/web/workspace/${hubId}`);
        this.$toast?.success("Success", "Hub deleted successfully.");
        this.showDeleteHubModal = false;
        this.$router.push("/my-hubs");
      } catch (error) {
        console.error("Error deleting hub:", error);
        this.$toast?.error("Error", "Failed to delete hub.");
      }
    },
    formatDate(date) {
      return dayjs(date).fromNow();
    },
    getStatusClass(status) {
      const classes = {
        "In Progress":
          "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
        Planning:
          "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
        Completed:
          "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
      };
      return classes[status] || "bg-muted text-muted-foreground";
    },
    onBookMentor(bookingData) {
      console.log("Booking mentor with data:", bookingData);
      // Implementation for booking would go here
    },
    onEditGoal() {
      this.isEditingHub = true;
      this.showCreateHubModal = true;
    },
    formatOpeningHours(hours) {
      if (!hours || typeof hours !== "object") return "9:00AM to 5:00PM";
      const mon = hours.monday || hours.Monday;
      if (mon && mon !== "closed" && mon !== "Closed") {
        const parts = mon.split("-");
        if (parts.length === 2) {
          return `${this._formatTime(parts[0])} to ${this._formatTime(
            parts[1],
          )}`;
        }
      }
      return "9:00AM to 5:00PM";
    },
    _formatTime(timeStr) {
      if (!timeStr) return "";
      const [hour, min] = timeStr.trim().split(":");
      const h = parseInt(hour);
      const ampm = h >= 12 ? "PM" : "AM";
      const h12 = h % 12 || 12;
      return `${h12}:${min || "00"}${ampm}`;
    },
    formatPricing(pricing) {
      if (!pricing) return "Free";
      if (pricing.is_free) return "Free";
      if (pricing.price_per_day) {
        const currency =
          pricing.currency === "₦" ? "₦" : pricing.currency || "";
        return `${currency}${pricing.price_per_day.toLocaleString()} Daily`;
      }
      return "Free";
    },
    async saveAvailability(updatedHours) {
      if (!this.selectedHub) return;
      this.isSavingAvailability = true;

      try {
        const payload = { ...this.selectedHub, operating_hours: updatedHours };
        await apiClient.put(`/web/workspace/${this.selectedHub.id}`, payload);

        // Update local state and exit edit mode
        this.selectedHub.operating_hours = updatedHours;
        this.$toast?.success(
          "Success",
          "Operating hours updated successfully.",
        );
      } catch (error) {
        console.error("Error saving availability:", error);
        this.$toast?.error("Error", "Failed to update operating hours.");
      } finally {
        this.isSavingAvailability = false;
      }
    },
    formatDateTime(date) {
      if (!date) return "";
      return dayjs(date).format("MMM DD, YYYY • h:mm A");
    },
    viewRequestDetails(request) {
      this.selectedRequest = request;
    },
    async onApproveRequest(request) {
      try {
        await apiClient.post(`/web/bookings/${request.id}/approve`);
        this.$toast?.success(
          "Request Approved",
          `Successfully approved ${request.user_name}'s request.`,
        );
        this.selectedRequest = null;
        this.fetchWorkspaceRequests();
      } catch (error) {
        console.error("Error approving request:", error);
        this.$toast?.error("Error", "Failed to approve request.");
      }
    },
    async onDeclineRequest(request) {
      try {
        await apiClient.post(`/web/bookings/${request.id}/decline`);
        this.$toast?.info(
          "Request Declined",
          `Declined ${request.user_name}'s request.`,
        );
        this.selectedRequest = null;
        this.fetchWorkspaceRequests();
      } catch (error) {
        console.error("Error declining request:", error);
        this.$toast?.error("Error", "Failed to decline request.");
      }
    },
  },
  mounted() {
    window.scrollTo(0, 0);
    this.fetchHubDetails();
    this.fetchAllHubs();
  },
  watch: {
    "$route.params.id": function () {
      this.fetchHubDetails();
    },
  },
};
</script>

<style scoped>
/* Additional styles if needed */
</style>
